/**
 * モックおみくじ生成。
 * Track B の Cloud Functions が動き出したらこのモジュール経由で fetch に差し替える。
 * インターフェースは仕様書 §03 / §02 に整合。
 */

export type Rarity =
  | "sue_kichi"
  | "kichi"
  | "chu_kichi"
  | "dai_kichi"
  | "kyo"
  | "dai_kyo"
  | "tokudai_kichi";

/**
 * 仕様 02_DATA_MODEL.md §2.3 準拠。"first_pair" は廃止し、
 * pair モードに isFirstPair フラグを併用する形に正規化。
 */
export type DrawMode = "solo" | "pair" | "unlock_special";

export type DrawResult = {
  rarity: Rarity;
  bodyText: string;
  bodyTextEn: string;
  themeAuto: string;
};

const BASE_PROBS: Record<Rarity, number> = {
  sue_kichi: 0.3,
  kichi: 0.3,
  chu_kichi: 0.2,
  dai_kichi: 0.1,
  kyo: 0.08,
  dai_kyo: 0.01,
  tokudai_kichi: 0.01,
};

function weightedPick<K extends string>(weights: Record<K, number>): K {
  const entries = Object.entries(weights) as [K, number][];
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.random() * total;
  for (const [k, w] of entries) {
    r -= w;
    if (r <= 0) return k;
  }
  return entries[entries.length - 1][0];
}

export function pickRarity(): Rarity {
  return weightedPick(BASE_PROBS);
}

/**
 * Phase 1 では Cloud Functions が未稼働のため、骨格テンプレを返す。
 * 仕様書 §03 §6-7 から数件抜粋。
 */
const TEMPLATES: Record<Rarity, Array<{ ja: string; en: string }>> = {
  sue_kichi: [
    {
      ja: "末吉。風はまだ南より、急がず歩めば道は拓けん。",
      en: "Lesser blessing. Winds still from the south. Walk slowly; the path opens.",
    },
    {
      ja: "末吉。霞のうちに芽あり。手を伸ばさば、触るるものあり。",
      en: "Lesser blessing. A bud hides in the mist. Reach out, and you shall touch it.",
    },
  ],
  kichi: [
    {
      ja: "吉。月の出を待て。やがて道に灯あり、迷うことなかれ。",
      en: "Blessing. Await the rising moon. A lamp appears on your road; do not stray.",
    },
    {
      ja: "吉。久しき問いに答え近し。耳を澄ませよ。",
      en: "Blessing. An old question nears its answer. Listen closely.",
    },
  ],
  chu_kichi: [
    {
      ja: "中吉。秋雨の音を聴け。胸の奥に久しく忘れし問いの種、芽吹かんとす。",
      en: "Middle blessing. Listen to autumn rain. A long-forgotten question stirs within.",
    },
    {
      ja: "中吉。{partner}との縁、深まる兆しあり。手を取れ、迷わず。",
      en: "Middle blessing. The bond with {partner} deepens. Take the hand, do not waver.",
    },
  ],
  dai_kichi: [
    {
      ja: "大吉。今ぞ進むべし。月光の下に編まれし糸あり、明日の風はやさしからん。",
      en: "Great blessing. Now is the time. A thread woven under moonlight; tomorrow's wind shall be gentle.",
    },
    {
      ja: "大吉。{partner}と歩む道、星々が照らさん。怖るるな。",
      en: "Great blessing. The path you walk with {partner} is lit by stars. Fear not.",
    },
  ],
  kyo: [
    {
      ja: "凶。波荒し、舟を出すべからず。されど嵐の去りし朝、見ゆるは新しき岸。",
      en: "Misfortune. Seas rough, do not sail. Yet when storm passes, a new shore appears.",
    },
    {
      ja: "凶。今は身を伏せよ。されど三たびの月のうちに、風向きは変わらん。",
      en: "Misfortune. Lie low for now. Yet within three moons, the wind shall turn.",
    },
  ],
  dai_kyo: [
    {
      ja: "大凶。封ぜられしものあり。されど夜は明け、灯はかならず点る。",
      en: "Greater misfortune. Something is sealed. Yet dawn comes, and the lamp shall be lit.",
    },
  ],
  tokudai_kichi: [
    {
      ja: "特大吉。千年に一度の風吹けり。今日この日、忘るるなかれ。",
      en: "Supreme blessing. A wind once in a thousand years. Forget not this day.",
    },
  ],
};

const THEMES = [
  "reflection",
  "connection",
  "courage",
  "rest",
  "change",
  "discovery",
] as const;
export type Theme = (typeof THEMES)[number];

export function pickTheme(): Theme {
  return THEMES[Math.floor(Math.random() * THEMES.length)];
}

export function drawOmikuji(opts?: {
  partnerName?: string;
  rarity?: Rarity;
}): DrawResult {
  const rarity = opts?.rarity ?? pickRarity();
  const pool = TEMPLATES[rarity];
  const tpl = pool[Math.floor(Math.random() * pool.length)];
  const partner = opts?.partnerName ?? "あの人";
  const partnerEn = opts?.partnerName ?? "them";
  return {
    rarity,
    themeAuto: pickTheme(),
    bodyText: tpl.ja.replace("{partner}", partner),
    bodyTextEn: tpl.en.replace("{partner}", partnerEn),
  };
}

export const RARITY_DISPLAY: Record<
  Rarity,
  { ja: string; en: string; color: string; glow: string; weight: number }
> = {
  sue_kichi: {
    ja: "末吉",
    en: "Lesser Blessing",
    color: "#1a1a1a",
    glow: "rgba(245,241,232,0.4)",
    weight: 0,
  },
  kichi: {
    ja: "吉",
    en: "Blessing",
    color: "#1a1a1a",
    glow: "rgba(192,192,192,0.5)",
    weight: 1,
  },
  chu_kichi: {
    ja: "中吉",
    en: "Middle Blessing",
    color: "#9f1d35",
    glow: "rgba(201,169,97,0.5)",
    weight: 2,
  },
  dai_kichi: {
    ja: "大吉",
    en: "Great Blessing",
    color: "#9f1d35",
    glow: "rgba(201,169,97,0.85)",
    weight: 3,
  },
  kyo: {
    ja: "凶",
    en: "Misfortune",
    color: "#1a1a1a",
    glow: "rgba(40,40,40,0.6)",
    weight: 4,
  },
  dai_kyo: {
    ja: "大凶",
    en: "Greater Misfortune",
    color: "#0a0608",
    glow: "rgba(0,0,0,0.7)",
    weight: 5,
  },
  tokudai_kichi: {
    ja: "特大吉",
    en: "Supreme Blessing",
    color: "#9f1d35",
    glow: "rgba(255,200,80,1)",
    weight: 6,
  },
};
